import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, ValidationErrors } from 'webapp-types';
import { BillingFeature, UserLevel } from 'webapp/enums/enums';
import { validateEmail } from 'webapp/utils/validate';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormErrorComponent from 'webapp/components/form-error';
import FormInputComponent from 'webapp/components/form/input';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';
import type { InviteUserData } from 'webapp/components/user/invite';
import UserRolesSelectDropdownComponent from 'webapp/components/user/roles-select-dropdown';

interface Args {
	onInvite: (users: InviteUserData[]) => void;
	onClose: () => void;
	onContentChange: () => void;
	submitInProgress: boolean;
	maximumNumberOfNewUsers: number;
	initialNumberOfNewUsers?: number;
	ignoreEmptyFields?: boolean;
}

export class Invitee {
	@tracked level: UserLevel;
	@tracked errors: ValidationErrors<'email' | 'level'> = {};

	@tracked private _email = '';

	constructor(level = UserLevel.EDITOR) {
		this.level = level;
	}

	set email(value: string) {
		this._email = value.trim();
	}

	get email(): string {
		return this._email;
	}
}

export default class UserAdminInviteComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;

	@tracked invitees: Invitee[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const initialNumberOfNewUsers = args.initialNumberOfNewUsers || 1;

		next(() => {
			Array.from({ length: initialNumberOfNewUsers }).forEach(() => {
				this.addNewRow();
			});
		});
	}

	get hasAnyPlanSpace(): boolean {
		return this.billingService.checkIfCan(BillingFeature.USERS);
	}

	get canAddRow(): boolean {
		return this.invitees.length < this.args.maximumNumberOfNewUsers && this.hasAnyPlanSpace;
	}

	get canDeleteItem(): boolean {
		return this.invitees.length > 1 && !this.args.submitInProgress;
	}

	get isAddSectionShown(): boolean {
		return this.invitees.length < this.args.maximumNumberOfNewUsers;
	}

	get ignoreEmptyFields(): boolean {
		return this.args.ignoreEmptyFields || false;
	}

	get inviteesToProcess(): Invitee[] {
		let invitees = this.invitees;
		if (this.ignoreEmptyFields) {
			invitees = this.invitees.filter((user) => user.email);
		}

		return invitees;
	}

	get isSubmitDisabled(): boolean {
		return !this.inviteesToProcess.length;
	}

	validateFields(): boolean {
		let errors = 0;

		const invitees = this.inviteesToProcess;

		invitees.forEach((user) => {
			user.errors = {};
			let emailErrorMsg = '';

			if (!user.email) {
				emailErrorMsg = 'Missing email';
			} else if (!validateEmail({ email: user.email }, 'email')) {
				emailErrorMsg = 'Invalid email';
			} else if (invitees.filter((item) => item.email === user.email).length > 1) {
				emailErrorMsg = 'Duplicated email';
			}

			if (emailErrorMsg) {
				user.errors = {
					...user.errors,
					email: [{ message: emailErrorMsg, name: 'general' }],
				};
				errors++;
			}

			if (!user.level) {
				user.errors = {
					...user.errors,
					level: [{ message: 'Missing user role', name: 'general' }],
				};
				errors++;
			}
		});

		return errors === 0;
	}

	@action
	selectUserRole(invitee: Invitee, option: LabelValuePairNumeric): void {
		invitee.level = option.value;
	}

	@action
	addNewRow(): void {
		if (this.canAddRow) {
			this.invitees = [...this.invitees, new Invitee()];
			this.args.onContentChange();
		}
	}

	@action
	deleteRow(item: Invitee): void {
		if (this.canDeleteItem) {
			this.invitees = this.invitees.filter((i) => i !== item);
			this.args.onContentChange();
		}
	}

	@action
	invite(): void {
		if (this.validateFields()) {
			const users: InviteUserData[] = this.inviteesToProcess.map((item) => ({
				email: item.email,
				level: item.level,
			}));
			this.args.onInvite(users);
		} else {
			next(this, this.args.onContentChange);
		}
	}
	<template>
		<div class="row row--no-margin">
			<div class="col">
				<label>Email address</label>
			</div>
			<div class="col">
				<label>User role</label>
			</div>
		</div>

		{{#each this.invitees as |item|}}
			<div
				class="row item
					{{if this.canDeleteItem 'item--show-delete'}}
					{{if item.errors.email.length 'item--has-errors'}}"
			>
				<div class="col">
					<FormInputComponent
						name="companyName"
						maxlength="50"
						placeholder="Email"
						disabled={{@submitInProgress}}
						@value={{item.email}}
						@autofocus={{true}}
						@hideFloatLabel={{true}}
					/>
					<RowErrorComponent @items={{item.errors.email}} />
					<FormErrorComponent @errors={{item.errors.email}} />
				</div>
				<div class="col">

					<UserRolesSelectDropdownComponent
						@selected={{item.level}}
						@isDisabled={{@submitInProgress}}
						@onChange={{fn this.selectUserRole item}}
					/>

					<RowErrorComponent @items={{item.errors.level}} />

					{{#if this.canDeleteItem}}
						<a role="button" {{on "click" (fn this.deleteRow item)}}>
							<CommonUiIconComponent @name="Trash" @size="sm" />
						</a>
					{{/if}}
				</div>
			</div>
		{{/each}}

		{{#if this.isAddSectionShown}}
			<div class="row">
				<div class="add {{unless this.canAddRow 'add--no-space'}}">
					<a role="button" class="label" {{on "click" this.addNewRow}}>+ Add another user</a>

					{{#unless this.hasAnyPlanSpace}}
						<span class="info-tooltip">
							<CommonUiIconComponent @name="InfoFill" @size="sm" />
							<ToolTipComponent
								@text="Go to &quot;Account details&quot; &gt; &quot;Plans & billing&quot; to buy more users licenses."
								@position="right"
								@theme="light"
								@maxWidth={{300}}
								@delay={{1}}
							/>
						</span>
					{{/unless}}
				</div>
			</div>
		{{/if}}

		<div class="controls2">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{@onClose}}
				/>
				<SubmitButtonComponent
					@text="Invite"
					@extraClass="save"
					@onTrigger={{this.invite}}
					@submitInProgress={{@submitInProgress}}
					@isDisabled={{this.isSubmitDisabled}}
				/>
			</div>
		</div>
	</template>
}
